import * as React from 'react'

import { Fab, Box, Icon, styled, useTheme } from '@design/pt-ui'

import { Creative, CreativeAction } from '../interfaces/entities/creative'
import CreativeActions from '../components/CreativeActions'

import CallToAction from '../components/CallToAction'

import {
  IFormValidation,
  FormBlueprint,
} from '../interfaces/components/dynamic-form'
import ValidationMessages from '../components/ValidationMessages'
import * as DynamicFormHelpers from '../lib/dynamic-form/helpers'
import { translateWithUserLocale } from '../utils/i18n'

const ActionFab = styled(Fab)(({ theme }) => ({
  margin: theme.spacing(1),
  fontSize: 11,
  backgroundColor: '#fff',
}))

const ActionBtnIcon = styled(Icon)(({ theme }) => ({
  marginRight: theme.spacing(0.5),
}))

const StatusRow = styled('div')({
  display: 'flex',
  flexFlow: 'row',
  alignItems: 'center',
  textAlign: 'right',
})

interface Props {
  creative: Creative | null
  validating: boolean
  validation: IFormValidation
  isValidationDone: boolean
  dirty: boolean
  blueprint: FormBlueprint | null
  doCreativeAction: (creative: Creative, action: CreativeAction) => void
  onSave: () => any
  onFinalize: () => any
  onValidate: () => any
}

function CreativeActionButtons(props: Props) {
  const theme = useTheme()
  const creative = props.creative
  const fields = props.validation.fields
  const numberOfWarnings = DynamicFormHelpers.countWarningsAndErrors(
    props.validation,
  ).warnings
  const numberOfErrors = DynamicFormHelpers.countWarningsAndErrors(
    props.validation,
  ).errors

  const warnings =
    props.blueprint === null ? undefined : (
      <ValidationMessages fields={fields} blueprint={props.blueprint} />
    )

  if (creative == null) {
    return null
  }

  const iconSx = {
    fontSize: 44,
    mr: 2,
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexFlow: 'row',
        alignItems: 'center',
        textAlign: 'right',
      }}>
      {creative.status.name === 'work_in_progress' ? (
        <ActionFab
          variant="extended"
          disabled={!props.dirty}
          size="large"
          className="cp-creative-action-save"
          onClick={props.onSave}>
          <ActionBtnIcon fontSize="large">save</ActionBtnIcon>
          {props.dirty
            ? translateWithUserLocale('creative_details.save_current_progress')
            : translateWithUserLocale('saved')}
        </ActionFab>
      ) : null}
      {creative.status.name === 'specification_finalized' ? (
        <StatusRow>
          <Icon
            sx={{
              ...iconSx,
              color: theme.palette.custom.statusIcon.finalized,
            }}>
            check_circle_outline
          </Icon>
          <span>{translateWithUserLocale('specification_finalized')}</span>
        </StatusRow>
      ) : null}
      {creative.status.name === 'archived' ? (
        <StatusRow>
          <Icon
            sx={{
              fontSize: 44,
              mr: 1.5,
              color: theme.palette.custom.statusIcon.archived,
            }}>
            archive
          </Icon>
          <Box
            component="span"
            sx={{ color: theme.palette.custom.statusIcon.archived }}>
            Archived
          </Box>
        </StatusRow>
      ) : null}
      {creative.status.name === 'sent_to_agency' ? (
        <StatusRow>
          <Icon sx={{ ...iconSx, color: theme.palette.custom.statusIcon.sent }}>
            send
          </Icon>
          <span>{translateWithUserLocale('sent')}</span>
        </StatusRow>
      ) : null}
      {creative.status.name === 'outdated' ? (
        <StatusRow>
          <Icon
            sx={{
              ...iconSx,
              color: theme.palette.custom.statusIcon.outdated,
            }}>
            timelapse
          </Icon>
          <Box
            component="span"
            sx={{
              color: theme.palette.custom.statusIcon.outdated,
              lineHeight: '0.5',
            }}>
            {translateWithUserLocale('outdated')}
          </Box>
        </StatusRow>
      ) : null}
      {creative.possibleActions
        .map((action) => action.name)
        .includes('finalize') ? (
        <>
          <CallToAction
            onClick={
              props.isValidationDone && numberOfErrors === 0
                ? props.onFinalize
                : props.onValidate
            }
            confirm={numberOfErrors === 0 && numberOfWarnings > 0}
            disabled={props.validating || numberOfErrors > 0}
            dialog={{
              title: translateWithUserLocale(
                'confirmation_dialog.finalize_title',
              ),
              yes: translateWithUserLocale('finalize'),
              message: warnings,
            }}>
            <ActionFab
              variant="extended"
              size="large"
              className="cp-creative-action-finalize"
              disabled={numberOfErrors > 0 ? true : false}>
              <ActionBtnIcon fontSize="large">check</ActionBtnIcon>
              {translateWithUserLocale('action.finalize_specification')}
            </ActionFab>
          </CallToAction>
        </>
      ) : null}

      <CreativeActions
        creative={creative}
        doCreativeAction={props.doCreativeAction}
      />
    </Box>
  )
}

export default CreativeActionButtons
